import { getActiveLocale } from '@marine/i18n';

/**
 * The parts of a `typed-openapi` fetcher that are the same whichever API is
 * being called: turning the generated `EndpointParameters` into a body and a set
 * of headers.
 *
 * Everything a fetcher does *besides* this differs per API — which credential to
 * attach, and what a failure response means — so those stay in the per-API
 * fetchers rather than becoming options here. This module is deliberately the
 * boring half.
 *
 * The input is typed structurally rather than against one generated client's
 * `EndpointParameters`: every generated client emits the same
 * `{ body?, query?, header?, path? }` shape, and importing one client's version
 * here would make this module belong to that API.
 */

export type RestFetcherParameters = {
  body?: unknown;
  query?: Record<string, unknown>;
  header?: Record<string, unknown>;
  path?: Record<string, unknown>;
};

export type RestFetcherInput = {
  method: string;
  url: URL;
  urlSearchParams?: URLSearchParams | undefined;
  parameters?: RestFetcherParameters | undefined;
  path: string;
  overrides?: RequestInit;
  throwOnStatusError?: boolean;
};

const METHODS_WITH_BODY = ['post', 'put', 'patch', 'delete'];

export const isFormDataBody = (input: RestFetcherInput): boolean => input.parameters?.body instanceof FormData;

/**
 * `FormData` is passed through untouched and *without* a `Content-Type`: the
 * browser has to set it itself, because only it knows the multipart boundary.
 */
export const buildRequestBody = (input: RestFetcherInput, headers: Headers): string | FormData | undefined => {
  if (!METHODS_WITH_BODY.includes(input.method.toLowerCase())) {
    return undefined;
  }

  if (isFormDataBody(input)) {
    return input.parameters?.body as FormData;
  }

  if (input.parameters?.body) {
    headers.set('Content-Type', 'application/json');

    return JSON.stringify(input.parameters.body);
  }

  return undefined;
};

/** Per-operation headers the caller declared on the endpoint. `null` values are dropped, not sent empty. */
export const applyDeclaredHeaders = (input: RestFetcherInput, headers: Headers): void => {
  if (!input.parameters?.header) {
    return;
  }

  for (const [key, value] of Object.entries(input.parameters.header)) {
    if (value != null) {
      headers.set(key, String(value));
    }
  }
};

/**
 * The language the UI is in, so the backend answers in it: validation messages, sync errors and
 * limit messages come back already translated.
 *
 * Sent explicitly because neither platform's default is right. React Native sends nothing useful,
 * and a browser sends its own language list, which is not the language the user chose in the app.
 * The backend's locale middleware (MDC-4665) is to prefer the user's stored language and fall back
 * to this header, which covers the requests made before there is a user — and a language the user
 * has just switched to, before the stored one catches up.
 *
 * Set before the declared headers, so an endpoint that declares its own `Accept-Language` wins.
 */
export const applyLanguageHeader = (headers: Headers): void => {
  headers.set('Accept-Language', getActiveLocale());
};
